import { forwardRef } from "react";
import type { SelectHTMLAttributes } from "react";
import { get } from "react-hook-form";
import type { FieldErrors, FieldValues } from "react-hook-form";
import cx from "classnames";
import { useTranslation } from "next-i18next";

import { Label } from "common/ui/Label";
import { ErrorDisplay } from "common/ui/ErrorDisplay";
import { Icon } from "common/ui/Icon";
import { Stack } from "common/ui/Stack";
import { MoreInfoIcon } from "common/ui/MoreInfoIcon";

import styles from "./Select.module.scss";

interface Props extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string,
  errors?: FieldErrors<FieldValues>,
  disablePlaceholder?: boolean,
  helpMessage?: React.ReactNode,
  readOnly?: boolean
};

export const Select = forwardRef<HTMLSelectElement, Props>(({
  name,
  label,
  errors,
  placeholder,
  children,
  className,
  disablePlaceholder = true,
  value,
  helpMessage,
  readOnly,
  required,
  ...rest
}, ref) => {
  const { t } = useTranslation(["common", "validation"]);
  const error = get(errors, name);

  return (
    <div className={cx(styles.SelectContainer, className)}>
      {label && (
        <Stack gap={8}>
          <label htmlFor={name}>
            <Label variant="l4">
              {label}
              {required && <span className={styles.Required}>*</span>}
            </Label>
          </label>
          {helpMessage && (
            <MoreInfoIcon message={helpMessage} />
          )}
        </Stack>
      )}
      <div className={styles.Select}>
        <select
          ref={ref}
          className={cx({
            [styles.Error]: error,
            [styles.ReadOnly]: readOnly
           })}
          id={name}
          name={name}
          defaultValue={value !== undefined ? undefined : ""}
          value={value}
          required={required}
          {...rest}
        >
          <option value="" disabled={disablePlaceholder}>
            {placeholder ? placeholder : t("select")}
          </option>
          {children}
        </select>
        <span className={styles.Icon}>
          {readOnly ? (
            <Icon icon="lock" className={styles.LockIcon} />
          ) : (
            <Icon icon="dropdownMinor" />
          )}
        </span>
      </div>
      {error && (
        <ErrorDisplay error={
          error.message
            ? t(error.message, { ns: "validation" })
            : t(`${name}.${error.type}`, { ns: "validation" })
        } />
      )}
    </div>
  )
});

Select.displayName = "Select";

